import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { usePageTitle, useAlert } from '../hooks';
import TrialStatusModal from '../components/TrialStatusModal';
import { useIdeaResults } from '../hooks/useIdeaResults';
import IdeaResultsContainer from '../components/ideas/IdeaResultsContainer';
import IdeaActionsPanel from '../components/ideas/IdeaActionsPanel';

const IdeaResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: ideaId } = useParams<{ id: string }>();
  const [showStickyBar, setShowStickyBar] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use reusable hooks
  const { alertMessage, showAlert, showSuccess, showError, hideAlert } = useAlert();

  // Use custom hook for data fetching
  const {
    loading,
    ideaData,
    isExpiredModalOpen,
    isAdmin,
    closeExpiredModal
  } = useIdeaResults({ ideaId, onError: showAlert });

  // Dynamic page title based on idea generation status
  const pageTitle = React.useMemo(() => {
    if (!ideaData) return 'Idea Details';

    switch (ideaData.status) {
      case 'queued':
        return 'Queued - Ideas';
      case 'processing':
        return 'Generating Ideas...';
      case 'failed':
        return 'Failed - Ideas';
      default:
        return 'Idea Details';
    }
  }, [ideaData?.status]);

  usePageTitle(pageTitle);

  // Scroll handler for sticky CTA bar
  useEffect(() => {
    const handleScroll = () => {
      const firstCard = document.querySelector('[data-first-card]');
      if (firstCard) {
        const rect = firstCard.getBoundingClientRect();
        setShowStickyBar(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Redirect if no idea ID after loading
  useEffect(() => {
    if (!loading && !ideaData && !alertMessage) {
      navigate('/ideas', { replace: true });
    }
  }, [loading, ideaData, alertMessage, navigate]);

  // Event handlers
  const handleBackToIdeas = () => {
    navigate('/ideas');
  };

  const handleCopySuccess = (message: string) => {
    showSuccess(message);
  };

  const handleCopyError = (message: string) => {
    showError(message);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading idea details...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (!ideaData && alertMessage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity={alertMessage.type} sx={{ mb: 2 }}>
          {alertMessage.message}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/ideas')}>
          Back to Ideas
        </Button>
      </Box>
    );
  }

  if (!ideaData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Idea not found or you don't have permission to view it.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/ideas')}>
          Back to Ideas
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Alert message */}
      <Snackbar 
        open={!!alertMessage} 
        autoHideDuration={6000} 
        onClose={hideAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideAlert} 
          severity={alertMessage?.type || 'info'} 
          sx={{ width: '100%' }}
        >
          {alertMessage?.message || ''}
        </Alert>
      </Snackbar>
      
      {/* Trial Expired Modal */}
      <TrialStatusModal 
        isOpen={isExpiredModalOpen} 
        onClose={closeExpiredModal} 
        isAdmin={isAdmin}
        status="expired"
      />

      {/* Sticky CTA Bar for Mobile */}
      {isMobile && showStickyBar && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            zIndex: 1000,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToIdeas}
              sx={{ 
                fontSize: '0.875rem',
                minWidth: '100px',
                borderColor: '#012A4A',
                color: '#012A4A'
              }}
            >
              Back
            </Button>
          </Stack>
        </Box>
      )}

      <Box sx={{ 
        my: 4,
        px: { xs: 1, sm: 2, md: 0 }, // Reduced responsive padding
        pb: isMobile && showStickyBar ? '80px' : 0 // Padding for sticky bar
      }}>
        <Stack spacing={4}>
          {/* Main Content Container */}
          <IdeaResultsContainer
            ideaData={ideaData}
            ideaId={ideaId}
            onCopySuccess={handleCopySuccess}
            onCopyError={handleCopyError}
          />

          {/* Action Buttons */}
          <IdeaActionsPanel
            onBackToIdeas={handleBackToIdeas}
            loading={loading}
          />
        </Stack>
      </Box>
    </>
  );
};

export default IdeaResultsPage;